import type { MuiTelInputCountry } from "mui-tel-input";
import { useSettings } from "../useSetting";
import { ISetting } from "@/domain/setting.interface";

/**
 * Country the phone inputs fall back to when the account has no
 * `countryFormat.phoneDefaultCountry` configured. Matches the pre-SIMS-9094
 * hardcoded value, so US accounts are unaffected by the fix.
 */
export const DEFAULT_PHONE_COUNTRY: MuiTelInputCountry = "US";

/**
 * Resolve the phone input's country from an already-loaded settings object.
 *
 * Use this in components that ALREADY call `useSettings(userId)` — calling
 * `usePhoneDefaultCountry()` there would key a second React Query entry
 * (the hook keys on `userId`) and fire a redundant request.
 */
export const resolvePhoneDefaultCountry = (
	settings: ISetting | undefined,
): MuiTelInputCountry => {
	const configured = settings?.countryFormat?.phoneDefaultCountry;
	return configured
		? (configured as MuiTelInputCountry)
		: DEFAULT_PHONE_COUNTRY;
};

/**
 * Phone input country (flag + dial prefix) from the account's `countryFormat`
 * setting — SIMS-9094. For components that do not otherwise read settings.
 */
export const usePhoneDefaultCountry = (userId?: string): MuiTelInputCountry => {
	const { data: settings } = useSettings(userId);
	return resolvePhoneDefaultCountry(settings);
};
